import {
  getExpansionBrowseLocalesByCountry,
  isExpansionMarketCountryId,
  isExpansionMarketLocale,
  isExpansionMarketsOrderFlowEnabled,
} from '@/core/constants/expansion-markets';
import { isIso3166Alpha2CountryCode } from '@/core/constants/iso-country-codes';
import {
  buildGenericBrowseOnlyLocale,
  getNonEcommerceLocalesByCountry,
  getNonEcommerceMarketByLocale,
  isConfiguredNonEcommerceCountry,
  type NonEcommerceMarketLocale,
  pickLocaleForCountry,
  resolveConfiguredNonEcommerceLocale,
} from '@/core/constants/non-ecommerce-markets';
import {
  isSupportedLanguage,
  SUPPORTED_LANGUAGE_IDS,
} from '@/core/constants/supported-languages';
import parseLocale from '@/core/i18n/parse-locale';
import composeLocale from '@/core/utils/locale/compose-locale';

export type EvaOrganizationSummary = {
  countryId: string;
  languageId: string;
};

function normalizeCountryIds(evaCountryIds: readonly string[]): string[] {
  return evaCountryIds.map(id => id.toUpperCase());
}

function hasEvaOrganizationForCountry(
  countryId: string,
  evaCountryIds: readonly string[],
): boolean {
  const normalized = countryId.toUpperCase();
  return normalizeCountryIds(evaCountryIds).includes(normalized);
}

function findEvaOrganizationForCountry(
  countryId: string,
  organizations: readonly EvaOrganizationSummary[],
): EvaOrganizationSummary | undefined {
  const normalized = countryId.toUpperCase();
  return organizations.find(org => org.countryId.toUpperCase() === normalized);
}

/**
 * True when cart, checkout, account ordering, and EVA catalog must be disabled.
 *
 * When `evaCountryIds` is provided, locales whose country has no EVA organization are treated
 * as generic browse-only markets (geo-detected fallback).
 */
export function isBrowsingWithoutOrderFlowLocale(
  locale: string,
  evaCountryIds?: readonly string[],
): boolean {
  const normalized = locale.toLowerCase();

  if (getNonEcommerceMarketByLocale(normalized)) {
    return true;
  }

  if (isExpansionMarketLocale(normalized) && !isExpansionMarketsOrderFlowEnabled()) {
    return true;
  }

  if (evaCountryIds && evaCountryIds.length > 0) {
    const { country } = parseLocale(normalized);
    const countryCode = country.toUpperCase();
    if (
      isIso3166Alpha2CountryCode(countryCode) &&
      !hasEvaOrganizationForCountry(country, evaCountryIds)
    ) {
      return true;
    }
  }

  return false;
}

export type ResolvedLocaleRequest =
  | { action: 'serve'; locale: string }
  | { action: 'redirect'; locale: string };

/**
 * Normalises a requested `language-country` locale for proxy/layout handling.
 *
 * - Invalid ISO country → redirect to `defaultLocale` (e.g. en-zz → en-nl)
 * - Browse-only + unsupported language → redirect to en-{country} (e.g. ja-jp → en-jp)
 * - Browse-only + supported language → serve as-is (e.g. en-jp)
 * - EVA market + unsupported language → redirect to `defaultLocale`
 * - EVA market + supported language → serve as-is (e.g. en-nl)
 */
export function resolveRequestedLocale(
  locale: string,
  evaCountryIds: readonly string[],
  defaultLocale: string,
): ResolvedLocaleRequest {
  const normalized = locale.toLowerCase();
  const { language, country } = parseLocale(normalized);
  const countryCode = country.toUpperCase();

  if (!isIso3166Alpha2CountryCode(countryCode)) {
    return { action: 'redirect', locale: defaultLocale.toLowerCase() };
  }

  const browseOnly = isBrowsingWithoutOrderFlowLocale(normalized, evaCountryIds);

  if (browseOnly) {
    const canonicalLocale = isSupportedLanguage(language)
      ? normalized
      : `en-${countryCode}`.toLowerCase();

    if (canonicalLocale !== normalized) {
      return { action: 'redirect', locale: canonicalLocale };
    }

    return { action: 'serve', locale: canonicalLocale };
  }

  if (!isSupportedLanguage(language)) {
    return { action: 'redirect', locale: defaultLocale.toLowerCase() };
  }

  return { action: 'serve', locale: normalized };
}

export function isBrowseOnlyCountry(
  countryId: string,
  evaCountryIds: readonly string[],
): boolean {
  const normalized = countryId.toUpperCase();

  if (
    isExpansionMarketCountryId(normalized) &&
    !isExpansionMarketsOrderFlowEnabled()
  ) {
    return true;
  }

  if (isConfiguredNonEcommerceCountry(normalized)) {
    return true;
  }

  if (!evaCountryIds.length) {
    return false;
  }

  return !hasEvaOrganizationForCountry(normalized, evaCountryIds);
}

/**
 * True when the country appears in the language changer (EVA webshop, configured
 * non-ecommerce, or expansion browse-only). Geo-detected countries without an
 * explicit market (e.g. Mauritius) are not selectable and must not trigger the
 * country detection modal.
 */
export function isCountrySelectableInLanguageChanger(
  countryId: string,
  evaCountryIds: readonly string[],
): boolean {
  const normalized = countryId.toUpperCase();

  if (hasEvaOrganizationForCountry(normalized, evaCountryIds)) {
    return true;
  }

  if (isConfiguredNonEcommerceCountry(normalized)) {
    return true;
  }

  if (
    isExpansionMarketCountryId(normalized) &&
    !isExpansionMarketsOrderFlowEnabled()
  ) {
    return true;
  }

  return false;
}

/** Selectable without the EVA organizations list (configured / expansion browse markets). */
export function isStaticallySelectableCountry(countryId: string): boolean {
  return isCountrySelectableInLanguageChanger(countryId, []);
}

/**
 * Locales available in the language changer for a browse-only country.
 * Configured non-ecommerce → expansion browse → generic supported languages.
 */
export function getBrowseOnlyLocalesForCountry(
  countryCode: string,
  evaCountryIds: readonly string[],
): NonEcommerceMarketLocale[] {
  const normalized = countryCode.toUpperCase();

  if (!isBrowseOnlyCountry(normalized, evaCountryIds)) {
    return [];
  }

  const configured = getNonEcommerceLocalesByCountry(normalized);
  if (configured.length > 0) {
    return configured;
  }

  const expansion = getExpansionBrowseLocalesByCountry(normalized);
  if (expansion.length > 0) {
    return expansion;
  }

  return SUPPORTED_LANGUAGE_IDS.flatMap(languageId => {
    const locale = composeLocale(languageId, normalized);
    return locale
      ? [{ languageId, languageName: languageId, locale }]
      : [];
  });
}

/**
 * Resolves the best locale for a geo-detected country.
 *
 * Priority:
 * 1. Expansion browse-only markets (UK/CH when order-flow flag is off)
 * 2. Explicitly configured non-ecommerce markets
 * 3. EVA webshop markets
 * 4. Generic browse-only fallback (`{lang}-{country}`)
 */
export function resolveLocaleForDetectedCountry(
  countryId: string,
  browserLang: string | undefined,
  organizations: readonly EvaOrganizationSummary[],
  fallbackLocale: string,
): string {
  const normalizedCountry = countryId.toUpperCase();
  const { language: fallbackLanguage } = parseLocale(fallbackLocale);
  const defaultLanguage = browserLang ?? fallbackLanguage;

  if (
    isExpansionMarketCountryId(normalizedCountry) &&
    !isExpansionMarketsOrderFlowEnabled()
  ) {
    const browseLocales = getExpansionBrowseLocalesByCountry(normalizedCountry);
    return pickLocaleForCountry(browseLocales, browserLang, fallbackLocale);
  }

  const configuredLocale = resolveConfiguredNonEcommerceLocale(
    normalizedCountry,
    browserLang,
    fallbackLocale,
  );
  if (configuredLocale) {
    return configuredLocale;
  }

  const organization = findEvaOrganizationForCountry(
    normalizedCountry,
    organizations,
  );
  if (organization) {
    const language = browserLang ?? organization.languageId.toLowerCase();
    return (
      composeLocale(language, organization.countryId) ??
      fallbackLocale.toLowerCase()
    );
  }

  if (!isIso3166Alpha2CountryCode(normalizedCountry)) {
    return fallbackLocale.toLowerCase();
  }

  return buildGenericBrowseOnlyLocale(
    normalizedCountry,
    browserLang,
    defaultLanguage,
  );
}
